// Shared helpers for affiliate product-card CTAs.
//
// The growth playbook's specificity rule: a CTA should name the product
// ("See Andersons PGF Complete on Amazon"), not say a generic "Check price on
// Amazon". Product names are often full Amazon titles ("... Advanced 16-4-8
// Liquid Fertilizer (32 oz)"), too long for a button, so we derive a short,
// still-recognizable label from the first few words.

/**
 * Derive a short, button-friendly product label from a full product name.
 * Keeps the first few words so the label still names the actual product; strips
 * a trailing punctuation/paren fragment if the cut lands mid-spec. Callers should
 * prefer an authored short name and fall back to this.
 */
export function deriveShortName(name: string): string {
  let words = name.trim().split(/\s+/)
  if (words.length > 4) {
    words = words.slice(0, 4)
    // If the 4th word is a parenthetical/size fragment ("(32", "(Bag)"), drop it —
    // it reads as noise once detached from the rest of the spec.
    if (/^[([]/.test(words[words.length - 1])) words = words.slice(0, -1)
  }
  return words
    .join(' ')
    .replace(/,(?=\s|$)/g, '') // drop word-end commas ("Builder, Weed" → "Builder Weed")
    .replace(/[([,]+$/, '') // trim a dangling open bracket/paren/comma
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Merchant name for a "Check price on {merchant}" CTA, derived from the href
 * host. Amazon regional TLDs all read "Amazon"; direct programs get their brand;
 * anything else falls back to the bare hostname so the button never claims
 * the wrong store.
 */
export function merchantLabelForHref(href: string): string {
  let host = ''
  try {
    host = new URL(href).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return 'Amazon'
  }
  if (host === 'amzn.to' || /(^|\.)amazon\.[a-z.]{2,}$/.test(host)) return 'Amazon'
  if (host === 'andersonslawn.com') return 'Andersons'
  return host
}
